import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import {
  Box,
  HStack,
  List,
  ListItem,
  Text,
  Textarea,
  TextareaProps,
  useDisclosure
} from "@chakra-ui/react";

import { User } from "@/jenesys-types/jenesys-entities/config";

import { MentionBadge } from "./MentionBadge";

interface CommentInputProps extends Omit<TextareaProps, "onChange"> {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  placeholder?: string;
  companyMembers: User[];
  isDisabled?: boolean;
  initialRows?: number;
}

export const CommentInput: React.FC<CommentInputProps> = ({
  value,
  onChange,
  onSend,
  placeholder = "Type your reply...",
  companyMembers,
  isDisabled = false,
  initialRows = 1,
  fontSize = "sm",
  ...props
}) => {
  const [cursorPosition, setCursorPosition] = useState(0);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Dynamically resize the textarea element based on its content.
  const autoResize = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;

    const fontSizePx = parseFloat(
      window.getComputedStyle(document.documentElement).fontSize
    );
    const maxHeight = (Number.isNaN(fontSizePx) ? 16 : fontSizePx) * 6; // 6rem

    el.style.height = "auto";
    const newHeight = Math.min(el.scrollHeight, maxHeight);
    el.style.height = `${newHeight}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? "auto" : "hidden";
  }, []);

  useEffect(() => {
    autoResize();
  }, [value, autoResize]);

  // Scroll selected suggestion into view
  const scrollSelectedIntoView = useCallback(() => {
    if (suggestionsRef.current && selectedSuggestionIndex >= 0) {
      const container = suggestionsRef.current;
      const listItems = container.querySelectorAll("[data-suggestion-item]");
      const selectedElement = listItems[selectedSuggestionIndex] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({
          block: "nearest",
          behavior: "smooth"
        });
      }
    }
  }, [selectedSuggestionIndex]);

  // Find @ mentions in the current text
  const currentMention = useMemo(() => {
    const beforeCursor = value.substring(0, cursorPosition);

    // Check for simple mention pattern: @username
    const simpleMatch = beforeCursor.match(/@([a-zA-Z0-9_]*)$/);
    if (simpleMatch) {
      return simpleMatch[1];
    }

    return null;
  }, [value, cursorPosition]);

  const filteredMembers = useMemo(() => {
    const beforeCursor = value.substring(0, cursorPosition);
    const lastIncompleteMention = beforeCursor.match(/@[a-zA-Z0-9_]*$/);
    const hasIncompleteMention = !!lastIncompleteMention;
    const endsWithAt = beforeCursor.endsWith("@");
    const hasAtSymbol = endsWithAt;

    if (!hasIncompleteMention && !hasAtSymbol) {
      return [];
    }

    if (currentMention === null && hasAtSymbol) {
      return companyMembers.slice(0, 10);
    }

    return companyMembers
      .filter((member) => {
        const fullName =
          `${member.first_name} ${member.last_name}`.toLowerCase();
        const firstName = member.first_name.toLowerCase();
        const lastName = member.last_name.toLowerCase();
        const searchTerm = currentMention?.toLowerCase() || "";

        return (
          fullName.includes(searchTerm) ||
          firstName.includes(searchTerm) ||
          lastName.includes(searchTerm)
        );
      })
      .slice(0, 10);
  }, [currentMention, companyMembers, value, cursorPosition]);

  // Auto-scroll when selection changes
  useEffect(() => {
    if (isOpen && filteredMembers.length > 0) {
      scrollSelectedIntoView();
    }
  }, [
    selectedSuggestionIndex,
    isOpen,
    filteredMembers.length,
    scrollSelectedIntoView
  ]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      onChange(newValue);
      setCursorPosition(e.target.selectionStart || 0);
      const beforeCursor = newValue.substring(0, e.target.selectionStart || 0);
      const lastIncompleteMention = beforeCursor.match(/@[a-zA-Z0-9_]*$/);
      const hasIncompleteMention = !!lastIncompleteMention;
      const endsWithAt = beforeCursor.endsWith("@");
      const hasAtSymbol = endsWithAt;

      if (hasIncompleteMention || hasAtSymbol) {
        onOpen();
        setSelectedSuggestionIndex(0);
      } else {
        onClose();
        setSelectedSuggestionIndex(0);
      }

      autoResize();
    },
    [onChange, onOpen, onClose, autoResize]
  );

  const handleMemberSelect = useCallback(
    (member: User) => {
      const beforeMention = value.substring(0, cursorPosition);
      const afterMention = value.substring(cursorPosition);

      // Handle mention pattern: @jano -> @"John Doe"
      const beforeAt = beforeMention.replace(/@[a-zA-Z0-9_]*$/, "");
      const newValue = `${beforeAt}@"${member.first_name} ${member.last_name}" ${afterMention}`;

      onChange(newValue);
      onClose();

      setTimeout(() => {
        if (inputRef.current) {
          const input = inputRef.current;
          input.focus();
          const newPosition = newValue.length - afterMention.length;
          input.setSelectionRange(newPosition, newPosition);
          autoResize();
        }
      }, 10);
    },
    [value, cursorPosition, onChange, onClose, autoResize]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (isOpen && filteredMembers.length > 0) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setSelectedSuggestionIndex((prev) =>
            prev < filteredMembers.length - 1 ? prev + 1 : 0
          );
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          setSelectedSuggestionIndex((prev) =>
            prev > 0 ? prev - 1 : filteredMembers.length - 1
          );
        } else if (e.key === "Enter") {
          e.preventDefault();
          handleMemberSelect(filteredMembers[selectedSuggestionIndex]);
        } else if (e.key === "Escape") {
          e.preventDefault();
          onClose();
          setSelectedSuggestionIndex(0);
        }
      } else if (e.key === "Enter" && !e.shiftKey && !isOpen) {
        e.preventDefault();
        onSend();
      }
    },
    [
      onSend,
      isOpen,
      filteredMembers,
      selectedSuggestionIndex,
      handleMemberSelect,
      onClose
    ]
  );

  return (
    <Box w="full" position="relative">
      <Textarea
        rows={initialRows}
        minH={10}
        maxH="6rem"
        fontSize={fontSize}
        ref={inputRef}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        isDisabled={isDisabled}
        resize="none"
        style={{ overflowY: "hidden" }}
        data-comment-input
        {...props}
      />

      {isOpen && filteredMembers.length > 0 && (
        <Box
          ref={suggestionsRef}
          position="absolute"
          top="100%"
          left={0}
          right={0}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          boxShadow="lg"
          zIndex={1000}
          maxH="200px"
          overflowY="auto"
        >
          <List>
            {filteredMembers.map((member, index) => (
              <ListItem
                key={member.id}
                data-suggestion-item
                px={3}
                py={2}
                cursor="pointer"
                bg={
                  index === selectedSuggestionIndex
                    ? "primary.50"
                    : "transparent"
                }
                _hover={{
                  bg:
                    index === selectedSuggestionIndex
                      ? "primary.100"
                      : "gray.50"
                }}
                onClick={() => handleMemberSelect(member)}
              >
                <HStack spacing={2}>
                  <MentionBadge user={member} size="sm" />
                  <Text fontSize="sm" color="gray.600">
                    {member.email}
                  </Text>
                </HStack>
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Box>
  );
};
